// public/v3/src/push-modal.jsx — two-stage Push-to-EMR dashboard.
//
// Phases:
//   loading          — fetching billedState + billingNotesMap
//   preflight        — eligibility breakdown + primary action button
//   review-99487     — pre-generation review for 99487 patients with no new data
//   generating       — per-row Generate progress
//   generated        — "N generated. Review in CCM, then Push & Sign." prompt
//   pushing-signing  — per-row Push & Sign progress
//   done             — final summary
//
// The "mode" determines what preflight + the Push button trigger:
//   - 'generate' — preflight buckets generated vs not-yet-generated; primary
//     action is Generate (followed by review-prompt). The review-99487 gate
//     fires before generate (when the CMS substantial-revision decision is
//     made).
//   - 'pushsign' — preflight buckets ready-to-ship; primary action is
//     Push & Sign (atomic push + PATCH-sign in the same loop).
//
// On open: smart-skip empty phases. If anyone needs Generate → mode=generate.
// Else if anyone needs Push & Sign → mode=pushsign. Else → mode=idle.
//
// Each `task` is { patientId, patient, program, code }.
// Row state is keyed by `${patientId}:${program}` so a dually-enrolled patient
// in a non-APCM month has two independent row states.

import React, { useState, useEffect, useMemo } from 'react';
import { ccmApi } from './lib/api';
import {
  generateForPatient,
  pushAndSignForPatient,
  getPushEligibility,
  getWorkflowState,
  check99487EligibilityBatch,
  checkTierBumpEligibilityBatch,
  writeCcmTierAffirmation,
  _resetPushEmrSettingsCache,
  checkBillLanded,
} from './lib/push-emr';
import { TierBumpReviewModal } from './components/tier-bump-review-modal';
import { deriveRowStatus } from './lib/push-row-status';
import { mapWithConcurrency, retryAsync } from './lib/async-pool';
import { collectBatchRecords, runLocalTasks, materializeBatchResults } from './lib/batch-careplan';

// Bulk generation runs a few patients at a time. generateForPatient touches
// only per-(patient,month) keys, so this is safe; the cap keeps us well under
// the server AI rate limits (500/user, 600/tenant per hour). PUSH stays
// sequential (it read-modify-writes the shared billed-state blob).
// Post-incident (2026-06-30): serialize bulk generation. Concurrency 3 drove Bedrock
// ThrottlingExceptions + DB pool (12-conn) exhaustion during the nightly run. "Better
// slow than error" — raise once the server-side Bedrock/DB concurrency gates land.
const GEN_CONCURRENCY = 1;
// Bedrock batch jobs have a hard 100-record floor and only pay off at panel
// scale, so the "Batch generate" action is surfaced — and actually enqueues —
// only at >= BATCH_MIN included records. Below it (or on server fallback) we
// just run the existing realtime generate path.
const BATCH_MIN = 100;
// Bedrock BATCH inference is a shelved WIP (migration 053 unapplied in prod), so the
// "Batch generate" enqueue entry point is hidden — operators stay on the realtime lane.
// (startBatchGenerate + the batch-consumption UI remain, dormant, for when it resumes.)
const BATCH_ENABLED = false;
// Errors that look transient (AI proxy 5xx / timeout / overload) — worth one
// auto-retry, since generation is idempotent (it overwrites the same keys).
const TRANSIENT_GEN_RE = /service error|timed out|temporar|overload|503|529|network|rate limit/i;

const STEP_LABEL = {
  'careplan':     'Generating care plan…',
  'billing-note': 'Generating billing note…',
  'billing-push': 'Pushing billing…',
  'phone-push':   'Pushing phone note…',
  'sign':         'Signing in Elation…',
  'billed-state': 'Marking billed…',
};

function fullName(p) {
  const raw = p?._raw || p || {};
  return `${raw.fname || ''} ${raw.lname || ''}`.trim() || p?.name || '(unnamed)';
}

function taskLabel(task) {
  const name = task.patient?.name || fullName(task.patient) || task.patientId;
  const slot = task.program === 'bhi' ? 'BHI' : 'CCM';
  return `${name} · ${slot} (${task.code})`;
}

function taskKey(task) {
  return `${task.patientId}:${task.program}`;
}

export function PushModal({ tasks, month, onClose, onDone, onJumpToTime, onJumpToApcm }) {
  const [phase, setPhase] = useState('loading');
  const [mode, setMode] = useState('idle'); // 'generate' | 'pushsign' | 'idle'
  const [billedState, setBilledState] = useState({});
  const [billingNotesMap, setBillingNotesMap] = useState({});
  const [rowState, setRowState] = useState({});
  const [results, setResults] = useState([]);
  const [reviewMap, setReviewMap] = useState({});
  const [reviewDecisions, setReviewDecisions] = useState({});
  const [reviewBusy, setReviewBusy] = useState(false);
  // CCM tier auto-bump pre-flight (runs BEFORE the 99487 review). tierBumpRows
  // is the per-candidate checklist data; reviewIncluded is the task list that
  // was carried into continueWith99487Review — used by applyReviewAndGenerate so
  // patient objects patched by writeCcmTierAffirmation are NOT discarded.
  const [tierBumpRows, setTierBumpRows] = useState(null);
  const [reviewIncluded, setReviewIncluded] = useState([]);
  // Batch lane (Task 9): startBatchGenerate enqueues the included generate
  // bucket as one async Bedrock batch job when >= BATCH_MIN records. batchJobId
  // /batchCount drive the 'batch-submitted' confirmation; batchError surfaces an
  // enqueue failure (after falling back to realtime).
  const [batchError, setBatchError] = useState('');
  // Billing review gates (tier-bump / 99487) fail OPEN on lookup errors by
  // design — but never silently: the operator must know a review was skipped.
  // A LIST — both lookups can fail in one run and neither may hide the other.
  const [gateWarnings, setGateWarnings] = useState([]);
  const [batchJobId, setBatchJobId] = useState('');
  const [batchCount, setBatchCount] = useState(0);
  // Batch consumption (Task 10): the tenant's batch jobs, listed in preflight.
  // Opening a done job materializes its results into the careplans store via
  // materializeBatchResults (no Elation push/sign) — the existing
  // generated/Push&Sign flow then takes over on the now-saved drafts.
  const [batchJobs, setBatchJobs] = useState([]);
  // Per-patient skip in the preflight: the user can exclude individual ready
  // tasks from the run. excludedKeys holds taskKey()s; includedTasks() narrows a
  // bucket to what will actually run and drives the counts + primary button.
  const [excludedKeys, setExcludedKeys] = useState(() => new Set());
  const toggleExcluded = (key) => setExcludedKeys((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const includedTasks = (bucket) => bucket.filter((t) => !excludedKeys.has(taskKey(t)));

  // Initial state load + mode selection.
  useEffect(() => {
    let cancelled = false;
    Promise.all([
      ccmApi.getBilledState().catch(() => ({})),
      ccmApi.listBillingNotes().catch(() => ({})),
    ]).then(([bs, bn]) => {
      if (cancelled) return;
      setBilledState(bs || {});
      const map = (bn && bn.billing_notes) ? bn.billing_notes : (bn || {});
      setBillingNotesMap(map || {});
      setPhase('preflight');
    });
    return () => { cancelled = true; };
  }, []);

  // Bucket tasks into generate vs pushsign vs skipped using the same
  // eligibility + workflow-state logic the chrome bar uses.
  const buckets = useMemo(() => {
    const toGenerate = [];
    const toPushSign = [];
    const skipped = [];
    for (const task of (tasks || [])) {
      const elig = getPushEligibility(task.patient, month, billedState, {
        ignoreAlreadySubmitted: true,
        program: task.program,
      });
      if (!elig.eligible && !elig.ready) {
        skipped.push({ task, reason: elig.reason });
        continue;
      }
      // Pass the task's program so BHI tasks bucket against the BHI slot
      // (bhi_bn key / BHI billed-state) rather than the CCM keys. Without it
      // getWorkflowState defaults to 'ccm' and a dual patient's 99484 is
      // dropped as "already submitted" when the CCM month is billed, or
      // mis-routed to Push & Sign. studio.jsx already threads program here.
      const ws = getWorkflowState(task.patient, month, billedState, billingNotesMap, { program: task.program });
      if (ws.needsGenerate) toGenerate.push(task);
      else if (ws.needsPushSign) toPushSign.push(task);
      else if (ws.isBilled) skipped.push({ task, reason: 'already submitted' });
    }
    return { toGenerate, toPushSign, skipped };
  }, [tasks, month, billedState, billingNotesMap]);

  // Pick the right mode whenever buckets change AND we're at preflight.
  // (Don't auto-flip mid-run.)
  useEffect(() => {
    if (phase !== 'preflight') return;
    if (buckets.toGenerate.length > 0) setMode('generate');
    else if (buckets.toPushSign.length > 0) setMode('pushsign');
    else setMode('idle');
  }, [phase, buckets.toGenerate.length, buckets.toPushSign.length]);

  // Batch consumption (Task 10): list the tenant's batch jobs. Guarded so a
  // missing table / route failure degrades to an empty list rather than
  // breaking the modal. Re-fetch on entering preflight (so a job that finished
  // while the modal was closed shows up) and after submitting a new batch job.
  useEffect(() => {
    if (phase !== 'preflight' && phase !== 'batch-submitted') return;
    let cancelled = false;
    ccmApi.getBatchCareplanJobs()
      .then((res) => { if (!cancelled) setBatchJobs((res && res.jobs) || []); })
      .catch(() => { if (!cancelled) setBatchJobs([]); });
    return () => { cancelled = true; };
  }, [phase]);

  // Entry: Generate. Runs the CCM tier-bump pre-flight FIRST (Standard-CCM
  // patients who logged >= 90 min can be affirmed up to 99487/99491), then
  // chains into the existing 99487 review. If there are no tier-bump
  // candidates, behavior is exactly as before — straight into the 99487 review.
  const startGenerate = async () => {
    const included = includedTasks(buckets.toGenerate);
    setGateWarnings([]);
    setReviewBusy(true);
    // CCM-only for the tier-bump pre-flight. checkTierBumpEligibilityBatch keys
    // on 99490-CCM enrollment, not the requested program, so a dually-enrolled
    // 99490 patient would otherwise surface in the checklist during a
    // standalone BHI-only run. Mirror studio.jsx's single-patient
    // `if (opts.program !== 'bhi')` gate by excluding BHI tasks here.
    const ccmIncluded = included.filter((t) => t.program !== 'bhi');
    let bumpMap = new Map();
    try {
      bumpMap = await checkTierBumpEligibilityBatch(ccmIncluded.map((t) => t.patient), month);
    } catch (_) {
      setGateWarnings((w) => [...w, "Couldn't check CCM tier-bump eligibility — proceeding without that review."]);
    }
    setReviewBusy(false);
    if (bumpMap.size === 0) {
      await continueWith99487Review(included);
      return;
    }
    // Build one checklist row per CCM candidate, pairing the eligibility hint
    // with the matching task's display name.
    const rows = [];
    for (const t of ccmIncluded) {
      const hint = bumpMap.get(t.patientId);
      if (!hint) continue;
      rows.push({
        patientId: t.patientId,
        name: t.patient?.name || fullName(t.patient) || t.patientId,
        minutes: hint.minutes,
        physicianPct: hint.physicianPct,
        // Prior same-month override (ccmTierMonth[month]) — seeds the modal's
        // radio state so a stale 99487/99491 bump renders checked instead of
        // "Keep 99490", and an untouched row emits a matching explicit entry.
        existingCode: hint.existingCode || null,
      });
    }
    setTierBumpRows(rows);
    setPhase('review-tier-bump');
  };

  // Persist each affirmation, threading the patched patient objects forward so
  // generate (and the 99487 apply path) bill against the affirmed tier. A failed
  // save keeps the original task (it just generates as 99490). Then continue
  // into the existing 99487 review with the SAME (possibly patched) task list.
  const applyTierBumpAndContinue = async (affirmByPatient) => {
    setReviewBusy(true);
    const included = includedTasks(buckets.toGenerate);
    const rowByPatient = {};
    for (const r of (tierBumpRows || [])) rowByPatient[r.patientId] = r;
    const updated = [];
    // A dual-enrolled patient (CCM + BHI) contributes TWO tasks with the same
    // patientId. writeCcmTierAffirmation must run at most ONCE per patient —
    // later tasks for the same patient reuse the already-patched object.
    const patchedByPatient = {};
    const written = new Set();
    for (const task of included) {
      const affirm = affirmByPatient && affirmByPatient[task.patientId];
      // No entry = not a checklist row (non-candidate task) -> leave as-is. The
      // modal seeds an explicit entry for EVERY displayed row, so a both-false
      // entry means "Keep 99490" (whether chosen or left seeded); we STILL call
      // writeCcmTierAffirmation({}) so any PRIOR same-month 99487/99491
      // override stored in ccmTierMonth[month] is CLEARED (writeCcmTierAffirmation
      // deletes the entry for an empty affirm, and no-ops when nothing is
      // stored). Otherwise a stale override would still bill the higher tier
      // despite the operator down-selecting to 99490.
      if (!affirm) { updated.push(task); continue; }
      if (written.has(task.patientId)) {
        const prior = patchedByPatient[task.patientId];
        updated.push(prior ? { ...task, patient: prior } : task);
        continue;
      }
      written.add(task.patientId);
      const row = rowByPatient[task.patientId] || {};
      try {
        const patched = await writeCcmTierAffirmation(task.patient, month, affirm, {
          affirmedBy: 'bulk operator',
          minutes: row.minutes,
          physicianPct: row.physicianPct,
        });
        patchedByPatient[task.patientId] = patched;
        updated.push({ ...task, patient: patched });
      } catch (_) {
        updated.push(task); // save failed — fall back to prior state for this patient
      }
    }
    setReviewBusy(false);
    setTierBumpRows(null);
    await continueWith99487Review(updated);
  };

  // The existing 99487 review gate, now parameterized by the (possibly
  // tier-bump-patched) task list rather than recomputing it from buckets.
  // check99487EligibilityBatch receives patient objects extracted from tasks;
  // only CCM patients enrolled in 99487 will surface in the review map.
  const continueWith99487Review = async (included) => {
    setReviewIncluded(included);
    setReviewBusy(true);
    let map = new Map();
    try {
      // CCM-only for the 99487 eligibility batch. check99487EligibilityBatch keys
      // on 99487-CCM enrollment, not the requested program, so a dual-enrolled
      // patient already on 99487 would otherwise surface the 99487 review during a
      // standalone BHI-only run. BHI tasks still thread through reviewIncluded and
      // pass through applyReviewAndGenerate (task.program === 'bhi'); we only scope
      // the eligibility lookup here. Mirrors the tier-bump pre-flight filter.
      const ccmIncluded = included.filter((t) => t.program !== 'bhi');
      map = await check99487EligibilityBatch(ccmIncluded.map((t) => t.patient), month);
    } catch (_) {
      setGateWarnings((w) => [...w, "Couldn't check 99487 eligibility — proceeding without that review."]);
    }
    setReviewBusy(false);
    if (map.size === 0) {
      await runGenerate(included, {});
      return;
    }
    const obj = {};
    const decisions = {};
    for (const [pid, elig] of map.entries()) {
      obj[pid] = elig;
      decisions[pid] = 'keep'; // default — preserve current 99487 enrollment
    }
    setReviewMap(obj);
    setReviewDecisions(decisions);
    setPhase('review-99487');
  };

  const applyReviewAndGenerate = async () => {
    setReviewBusy(true);
    const toRun = [];
    const freshnessByPatientId = {};
    const initialRowState = {};
    // Iterate the threaded list (carried in via continueWith99487Review), NOT a
    // fresh includedTasks(buckets.toGenerate) — otherwise the tier-bump-patched
    // patient objects from applyTierBumpAndContinue would be silently discarded.
    for (const task of reviewIncluded) {
      const p = task.patient;
      const elig = reviewMap[task.patientId];
      // BHI tasks and CCM tasks not in the review map pass through directly.
      if (!elig || task.program === 'bhi') { toRun.push(task); continue; }
      const decision = reviewDecisions[task.patientId] || 'keep';
      if (decision === 'skip') {
        initialRowState[taskKey(task)] = { status: 'skipped', error: 'skipped in 99487 review' };
        continue;
      }
      let taskToRun = task;
      if (decision === '99490' || decision === '99491') {
        const newProgram = decision;
        const prevProgram = (p._raw && p._raw.billing && p._raw.billing.program) || '';
        try {
          const nextRaw = {
            ...(p._raw || {}),
            billing: { ...((p._raw && p._raw.billing) || {}), program: newProgram },
          };
          const resp = await ccmApi.updatePatient(p.id, nextRaw);
          const stamped = { ...nextRaw, _updatedAt: (resp && resp.updatedAt) || nextRaw._updatedAt };
          taskToRun = { ...task, patient: { ...p, _raw: stamped } };
          try {
            await ccmApi.auditEvent(
              'COMPLEX_CCM_REVIEW_DOWNGRADE',
              'patients',
              task.patientId,
              `Complex CCM review for ${month}: downgraded ${prevProgram || '99487'} → ${newProgram} (no new labs/notes since ${elig.sinceDate || 'prior plan'})`,
            );
          } catch (_) {}
        } catch (e) {
          initialRowState[taskKey(task)] = { status: 'failed', error: 'downgrade failed: ' + (e?.message || 'unknown') };
          continue;
        }
      } else if (decision === 'keep') {
        try {
          await ccmApi.auditEvent(
            'COMPLEX_CCM_REVIEW_PROCEED',
            'patients',
            task.patientId,
            `Complex CCM review for ${month}: proceeded with 99487 despite no new labs/notes since ${elig.sinceDate || 'prior plan'}`,
          );
        } catch (_) {}
      }
      toRun.push(taskToRun);
      freshnessByPatientId[p.id] = elig.freshness;
    }
    setReviewBusy(false);
    await runGenerate(toRun, freshnessByPatientId, initialRowState);
  };

  const runGenerate = async (toRun, freshnessByPatientId, initialRowState) => {
    _resetPushEmrSettingsCache();
    setPhase('generating');
    const seed = { ...(initialRowState || {}) };
    for (const task of toRun) {
      const key = taskKey(task);
      if (!seed[key]) seed[key] = { status: 'pending' };
    }
    setRowState(seed);

    // Generate a few patients at a time (bounded concurrency) instead of one
    // strictly after another — generation is per-patient independent. Each task
    // auto-retries once on a transient AI failure (generation is idempotent).
    const out = await mapWithConcurrency(toRun, GEN_CONCURRENCY, async (task) => {
      const key = taskKey(task);
      setRowState((prev) => ({ ...prev, [key]: { status: 'running' } }));
      try {
        const res = await retryAsync(
          () => generateForPatient(task.patient, month, {
            program: task.program,
            freshness: freshnessByPatientId ? freshnessByPatientId[task.patientId] : undefined,
            onStep: ({ step }) => setRowState((prev) => ({
              ...prev,
              [key]: { status: 'running', step, error: prev[key]?.error },
            })),
          }),
          {
            attempts: 2,
            baseMs: 600,
            retryOnResult: (r) => r && !r.ok && (r.errors || []).some((e) => TRANSIENT_GEN_RE.test(String(e))),
          },
        );
        if (res.skipped) {
          setRowState((prev) => ({ ...prev, [key]: { status: 'skipped', error: res.reason } }));
        } else if (res.ok) {
          setRowState((prev) => ({ ...prev, [key]: { status: 'done' } }));
        } else {
          setRowState((prev) => ({ ...prev, [key]: { status: 'failed', error: res.errors.join('; ') } }));
        }
        return { patientId: task.patientId, program: task.program, taskKey: key, ...res };
      } catch (e) {
        setRowState((prev) => ({ ...prev, [key]: { status: 'failed', error: e?.message || 'unknown error' } }));
        return { patientId: task.patientId, program: task.program, taskKey: key, ok: false, results: {}, errors: [e?.message || 'unknown error'] };
      }
    });
    setResults(out);
    setPhase('generated');
    onDone?.(out);
  };

  // Batch lane (Task 9). Parallel entry to startGenerate/runGenerate: collect
  // the included generate bucket into LLM-bearing batch records + APCM/no-LLM
  // localTasks. The localTasks never batch — run them realtime now (instant).
  // Then: < BATCH_MIN records → realtime runGenerate (Bedrock won't take a tiny
  // batch); else enqueue one async batch job. A server-side 'fallback' OR any
  // enqueue throw degrades gracefully to the same realtime runGenerate. Leaves
  // runGenerate/runPushAndSign and the realtime flow untouched — this is additive.
  const startBatchGenerate = async () => {
    const included = includedTasks(buckets.toGenerate);
    if (included.length === 0) return;
    setBatchError('');
    setPhase('batch-collecting');
    let collected;
    try {
      collected = await collectBatchRecords(included, month);
    } catch (e) {
      setBatchError(e?.message || 'Failed to build batch');
      setPhase('preflight');
      return;
    }
    // APCM / no-LLM billing notes are assembled deterministically — run them
    // realtime now (they never enter the batch job). Non-fatal if any fail, but
    // surface failures instead of swallowing them: runLocalTasks isolates each
    // task and returns { ran, failed, errors } so a silently-skipped APCM note
    // is visible rather than lost.
    if (collected.localTasks.length) {
      try {
        const local = await runLocalTasks(collected.localTasks, month);
        if (local && local.failed > 0) {
          setBatchError(local.failed + ' local (APCM) note' + (local.failed === 1 ? '' : 's') + ' failed to generate; the batch will continue for the rest.');
        }
      } catch (e) {
        setBatchError(e?.message || 'Local (APCM) note generation failed');
      }
    }
    if (collected.records.length < BATCH_MIN) {
      // Below the Bedrock floor → just generate realtime as today.
      return runGenerate(included);
    }
    try {
      const res = await ccmApi.enqueueBatchCareplan(collected.records); // model resolved server-side
      if (res && res.mode === 'batch') {
        setBatchJobId(res.jobId);
        setBatchCount(res.count);
        setPhase('batch-submitted');
      } else {
        // Server fell back (e.g. batch unavailable) → realtime.
        return runGenerate(included);
      }
    } catch (e) {
      setBatchError(e?.message || 'Enqueue failed');
      return runGenerate(included);
    }
  };

  // Batch consumption (Task 10): open a done batch job → materialize its
  // results into the careplans store. This NEVER touches Elation — it only
  // writes via the careplans saves inside materializeBatchResults; the existing
  // generated → Push & Sign flow then ships the now-saved drafts. Reuses the
  // 'generating' running surface while the (fast, no-LLM) assembly + saves run.
  const materializeJob = async (job) => {
    setBatchError('');
    setPhase('generating');
    let rows = [];
    try {
      rows = (await ccmApi.getBatchCareplanResults(job.id)).records || [];
    } catch (e) {
      setBatchError(e?.message || 'Failed to fetch results');
      setPhase('preflight');
      return;
    }
    const patientsById = Object.fromEntries((tasks || []).map((t) => [t.patientId, t.patient]));
    let out;
    try {
      out = await materializeBatchResults(month, rows, patientsById);
    } catch (e) {
      // If materialize itself throws/rejects, don't strand the user on the
      // 'generating' surface — surface the error and return to preflight
      // (mirrors the results-fetch error path above).
      setBatchError(e?.message || 'Failed to materialize batch results');
      setPhase('preflight');
      return;
    }
    // PHI minimization — tear the job's stored results down once applied.
    // Best-effort: a prune failure must not block the user from the drafts.
    try { await ccmApi.pruneBatchCareplanJob(job.id); } catch (_) {}
    setResults([{ batch: true, ...out }]);
    setPhase('generated'); // lands on renderGenerated() summary
    onDone?.([]);          // let studio refresh drafts
  };

  const runPushAndSign = async (toRun) => {
    _resetPushEmrSettingsCache();
    setPhase('pushing-signing');
    const seed = {};
    for (const task of toRun) seed[taskKey(task)] = { status: 'pending' };
    setRowState(seed);

    // A reported 'failed' may be a false positive — the bill can be in Elation
    // even when the result is ok:false / threw (lost response, or a post-push
    // billed-state blip). Reconcile against the server's billingPush slot before
    // showing red.
    const reconcileFailed = async (st, task) => {
      if (st.status !== 'failed') return st;
      const landed = await checkBillLanded(task.patientId, month, task.program);
      if (landed === 'pushed') return { status: 'pushed_unsigned', error: 'already in Elation — verify/sign there' };
      if (landed === 'bill_failed') return { status: 'failed', needsVerify: true, error: 'note filed; bill failed — resolve in Elation' };
      return st;
    };

    const out = [];
    for (let i = 0; i < toRun.length; i++) {
      const task = toRun[i];
      const key = taskKey(task);
      setRowState((prev) => ({ ...prev, [key]: { status: 'running' } }));
      try {
        const res = await pushAndSignForPatient(task.patient, month, {
          program: task.program,
          onStep: ({ step }) => setRowState((prev) => ({
            ...prev,
            [key]: { status: 'running', step, error: prev[key]?.error },
          })),
        });
        // A successful push whose sign step failed is non-fatal (the bill is in
        // Elation, the note is unsigned) — surface it as 'pushed_unsigned', not
        // a misleading 'Done ✓'. deriveRowStatus encodes that distinction; then
        // reconcile any 'failed' against the server slot.
        const st = await reconcileFailed(deriveRowStatus(res), task);
        setRowState((prev) => ({ ...prev, [key]: st }));
        out.push({ patientId: task.patientId, program: task.program, taskKey: key, ...res });
      } catch (e) {
        const st = await reconcileFailed({ status: 'failed', error: e?.message || 'unknown error' }, task);
        setRowState((prev) => ({ ...prev, [key]: st }));
        out.push({ patientId: task.patientId, program: task.program, taskKey: key, ok: false, results: {}, errors: [e?.message || 'unknown error'], visitNoteIds: [], nonVisitNoteIds: [] });
      }
    }
    setResults(out);
    setPhase('done');
    onDone?.(out);
  };

  // ── Render helpers ────────────────────────────────────────────────────────
  function renderPreflight() {
    const { toGenerate, toPushSign, skipped } = buckets;
    if (mode === 'idle') {
      return <p>Nothing to do for {month} — every eligible patient is either submitted or in a non-actionable state.</p>;
    }
    const primaryBucket = mode === 'generate' ? toGenerate : toPushSign;
    const includedPrimary = includedTasks(primaryBucket);
    const otherBucket = mode === 'generate' ? toPushSign : toGenerate;
    const otherLabel = mode === 'generate' ? 'already drafted, ready to Push & Sign' : 'still need Generate';
    return (
      <>
        <p>
          {mode === 'generate'
            ? <>Ready to <b>generate</b>: <b>{includedPrimary.length}</b></>
            : <>Ready to <b>Push &amp; Sign</b>: <b>{includedPrimary.length}</b></>}
          {otherBucket.length > 0 && <> · {otherBucket.length} {otherLabel}</>}
          {skipped.length > 0 && <> · Skipped: <b>{skipped.length}</b></>}
        </p>
        {primaryBucket.length > 0 && (
          <details open>
            <summary>Included — {includedPrimary.length}</summary>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {primaryBucket.map((task) => {
                const k = taskKey(task);
                const isExcluded = excludedKeys.has(k);
                return (
                  <li key={k} style={{ marginBottom: 4, color: isExcluded ? 'var(--ink-3)' : undefined, textDecoration: isExcluded ? 'line-through' : 'none' }}>
                    {taskLabel(task)}
                    {' · '}
                    <button
                      className="btn ghost sm"
                      style={{ padding: '0 6px', fontSize: 12 }}
                      onClick={() => toggleExcluded(k)}>
                      {isExcluded ? 'Undo' : 'Skip'}
                    </button>
                  </li>
                );
              })}
            </ul>
          </details>
        )}
        {skipped.length > 0 && (
          <details>
            <summary>Skipped — {skipped.length}</summary>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {skipped.map(({ task, reason }) => (
                <li key={taskKey(task)} style={{ marginBottom: 4, color: 'var(--ink-3)' }}>
                  {taskLabel(task)} — {reason}
                  {reason.startsWith('Short') && onJumpToTime && (
                    <>
                      {' · '}
                      <button
                        className="btn ghost sm"
                        style={{ padding: '0 6px', fontSize: 12 }}
                        onClick={() => { onJumpToTime(task.patientId); onClose?.(); }}>
                        Log time →
                      </button>
                    </>
                  )}
                </li>
              ))}
            </ul>
          </details>
        )}
      </>
    );
  }

  // Batch consumption (Task 10): a compact list of the tenant's batch jobs.
  // Rendered in the preflight body regardless of mode (a finished batch job
  // typically lands when the generate bucket is empty → mode 'idle'). A done
  // job (or one with applied rows) offers Materialize; an in-flight one shows
  // its status. Empty → render nothing.
  function renderBatchJobs() {
    if (!batchJobs || batchJobs.length === 0) return null;
    return (
      <div style={{ marginTop: 16 }}>
        <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Batch jobs</div>
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
          {batchJobs.map((job) => {
            const ready = job.status === 'done' || (job.applied || 0) > 0;
            const inProgress = job.status === 'submitted' || job.status === 'running';
            return (
              <li key={job.id} style={{
                marginBottom: 6,
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: '8px 10px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r)',
              }}>
                <div style={{ flex: 1, fontSize: 13 }}>
                  <span style={{ textTransform: 'capitalize' }}>{job.status}</span>
                  {' · '}
                  <span style={{ color: 'var(--ink-3)' }}>
                    {(job.applied || 0)}/{(job.record_count || 0)} ready
                  </span>
                  {job.last_error && (
                    <span style={{ color: 'var(--bad)', fontSize: 12 }}> · {job.last_error}</span>
                  )}
                </div>
                {ready ? (
                  <button className="btn sm" onClick={() => materializeJob(job)}>
                    Materialize {(job.applied || job.record_count || 0)}
                  </button>
                ) : inProgress ? (
                  <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>In progress…</span>
                ) : null}
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  function renderRow(task, st) {
    const key = taskKey(task);
    const status = st?.status || 'pending';
    let label = 'Pending';
    let color = 'var(--ink-3)';
    if (status === 'running') {
      label = STEP_LABEL[st?.step] || 'Running…';
      color = 'var(--accent)';
    } else if (status === 'done') {
      label = 'Done ✓';
      color = 'var(--ok)';
    } else if (status === 'pushed_unsigned') {
      label = 'Pushed · sign in Elation ⚠';
      color = 'var(--warn)';
    } else if (status === 'failed') {
      label = 'Failed: ' + (st?.error || 'unknown');
      color = 'var(--bad)';
    } else if (status === 'skipped') {
      label = 'Skipped: ' + (st?.error || '');
      color = 'var(--ink-3)';
    }
    return (
      <li key={key} style={{ marginBottom: 6, display: 'flex', gap: 10, alignItems: 'baseline' }}>
        <span style={{ flex: 1 }}>{taskLabel(task)}</span>
        <span style={{ color, fontSize: 12 }}>{label}</span>
      </li>
    );
  }

  function renderReview99487() {
    // Only CCM tasks can be 99487; filter to tasks with a review entry.
    const rows = buckets.toGenerate.filter((task) => task.program !== 'bhi' && !!reviewMap[task.patientId]);
    const otherReady = buckets.toGenerate.length - rows.length;
    return (
      <>
        <p style={{ marginTop: 0 }}>
          <b>{rows.length}</b> patient{rows.length === 1 ? '' : 's'} enrolled in <b>99487 (Complex CCM)</b>{' '}
          {rows.length === 1 ? 'has' : 'have'} no new labs or office-visit notes since the prior care plan.
          CMS requires 99487 to include establishment or substantial revision of the care plan per service period —
          review each before generating:
        </p>
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0 0' }}>
          {rows.map((task) => {
            const elig = reviewMap[task.patientId];
            const decision = reviewDecisions[task.patientId] || 'keep';
            const p = task.patient;
            const raw = p?._raw || p || {};
            const name = ((raw.fname || '') + ' ' + (raw.lname || '')).trim() || p?.name || '(unnamed)';
            return (
              <li key={taskKey(task)} style={{
                marginBottom: 6,
                display: 'flex',
                gap: 10,
                alignItems: 'center',
                padding: '8px 10px',
                border: '1px solid var(--line)',
                borderRadius: 'var(--r)',
                background: decision === 'skip' ? 'var(--surface-2)' : 'var(--surface)',
              }}>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 14 }}>{name}</div>
                  <div style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    Prior plan: {elig.sinceDate || 'unknown'}
                  </div>
                </div>
                <select
                  value={decision}
                  disabled={reviewBusy}
                  onChange={(e) => setReviewDecisions((prev) => ({ ...prev, [task.patientId]: e.target.value }))}
                  style={{ fontSize: 13 }}>
                  <option value="keep">Keep 99487</option>
                  <option value="99490">Downgrade to 99490</option>
                  <option value="99491">Downgrade to 99491</option>
                  <option value="skip">Skip this generate</option>
                </select>
              </li>
            );
          })}
        </ul>
        {otherReady > 0 && (
          <p style={{ marginTop: 12, fontSize: 13, color: 'var(--ink-3)' }}>
            The remaining {otherReady} task{otherReady === 1 ? ' is' : 's are'} ready to generate.
          </p>
        )}
      </>
    );
  }

  function renderRunning() {
    const list = phase === 'generating' ? buckets.toGenerate : buckets.toPushSign;
    const done = Object.values(rowState).filter((s) => s.status === 'done' || s.status === 'pushed_unsigned' || s.status === 'failed' || s.status === 'skipped').length;
    return (
      <>
        <p>{phase === 'generating' ? 'Generating' : 'Pushing & signing'}… {done} / {list.length}</p>
        <progress value={done} max={list.length} style={{ width: '100%' }} />
        <ul style={{ listStyle: 'none', padding: 0, margin: '12px 0 0' }}>
          {list.map((task) => renderRow(task, rowState[taskKey(task)]))}
        </ul>
      </>
    );
  }

  // True when the generated summary came from materializing a batch job
  // (a single {batch:true, applied, failed, errors} row) rather than the
  // per-task realtime generate path. Drives a batch-aware summary + footer.
  const isBatchSummary = results.length === 1 && results[0] && results[0].batch === true;

  function renderGenerated() {
    if (isBatchSummary) {
      const { applied = 0, failed = 0, errors = [] } = results[0];
      return (
        <>
          <p>
            Materialized <b>{applied}</b> draft{applied === 1 ? '' : 's'} · Failed <b>{failed}</b>
          </p>
          <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
            Batch drafts are saved in CCM. Review the Care Plan and Billing Note tabs for each
            patient, then close and reopen Push to Elation to <b>Push &amp; Sign</b> the cohort.
          </p>
          {errors.length > 0 && (
            <details open>
              <summary style={{ color: 'var(--bad)' }}>Errors ({errors.length})</summary>
              <ul style={{ marginTop: 8, paddingLeft: 18 }}>
                {errors.map((e, i) => (
                  <li key={i} style={{ marginBottom: 4 }}>{String(e)}</li>
                ))}
              </ul>
            </details>
          )}
        </>
      );
    }
    const generated = results.filter((r) => r.ok && !r.skipped).length;
    const failed = results.filter((r) => !r.ok).length;
    const skippedCount = results.filter((r) => r.skipped).length;
    const failedRows = results.filter((r) => !r.ok);
    return (
      <>
        <p>
          Generated <b>{generated}</b> · Failed <b>{failed}</b> · Skipped <b>{skippedCount}</b>
        </p>
        <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
          Drafts are saved in CCM. Review the Care Plan and Billing Note tabs for each patient.
          When the cohort looks right, click <b>Push &amp; Sign {generated}</b> to ship to Elation.
        </p>
        {failedRows.length > 0 && (
          <details open>
            <summary style={{ color: 'var(--bad)' }}>Failed ({failedRows.length})</summary>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {failedRows.map((r) => {
                const task = (tasks || []).find((t) => t.patientId === r.patientId && t.program === r.program);
                return (
                  <li key={r.taskKey || r.patientId} style={{ marginBottom: 4 }}>
                    {task ? taskLabel(task) : r.patientId}: {(r.errors && r.errors.join('; ')) || 'unknown error'}
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </>
    );
  }

  function renderDone() {
    const pushed = results.filter((r) => r.ok && !r.skipped).length;
    const failed = results.filter((r) => !r.ok).length;
    const skippedCount = results.filter((r) => r.skipped).length;
    const failedRows = results.filter((r) => !r.ok);
    const pushedRows = results.filter((r) => r.ok && !r.skipped);
    const referralsDrafted = results.reduce((n, r) => n + (r.referralsDrafted || 0), 0);
    return (
      <>
        <p>
          Pushed &amp; signed <b>{pushed}</b> · Failed <b>{failed}</b> · Skipped <b>{skippedCount}</b>
          {referralsDrafted > 0 && <> · Referrals drafted <b>{referralsDrafted}</b></>}
        </p>
        {pushedRows.length > 0 && (
          <details open>
            <summary>Pushed ({pushedRows.length})</summary>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {pushedRows.map((r) => {
                const task = (tasks || []).find((t) => t.patientId === r.patientId && t.program === r.program);
                const p = task?.patient;
                const raw = p?._raw || {};
                const elationId = raw.elation_id;
                const signed = r.results && r.results.signed;
                return (
                  <li key={r.taskKey || r.patientId} style={{ marginBottom: 4 }}>
                    {task ? taskLabel(task) : r.patientId}{!signed && <span style={{ color: 'var(--bad)', fontSize: 12 }}> · unsigned</span>}
                    {elationId && (
                      <>
                        {' · '}
                        <a
                          href={`https://app.elationemr.com/patient/${encodeURIComponent(elationId)}/req-action/`}
                          target="_blank"
                          rel="noopener noreferrer"
                          style={{ fontSize: 12 }}>
                          Open in Elation →
                        </a>
                      </>
                    )}
                  </li>
                );
              })}
            </ul>
          </details>
        )}
        {failedRows.length > 0 && (
          <details open>
            <summary style={{ color: 'var(--bad)' }}>Failed ({failedRows.length})</summary>
            <ul style={{ marginTop: 8, paddingLeft: 18 }}>
              {failedRows.map((r) => {
                const task = (tasks || []).find((t) => t.patientId === r.patientId && t.program === r.program);
                return (
                  <li key={r.taskKey || r.patientId} style={{ marginBottom: 4 }}>
                    {task ? taskLabel(task) : r.patientId}: {(r.errors && r.errors.join('; ')) || 'unknown error'}
                  </li>
                );
              })}
            </ul>
          </details>
        )}
      </>
    );
  }

  // Backdrop click — block during in-flight work + during 99487 review apply.
  const backdropClick = (phase === 'generating' || phase === 'pushing-signing' || (phase === 'review-99487' && reviewBusy))
    ? undefined : onClose;

  const headTitle = phase === 'pushing-signing' || phase === 'done' || mode === 'pushsign'
    ? `Push & Sign · ${month}`
    : `Generate · ${month}`;

  // The tier-bump checklist is a standalone modal (it brings its own backdrop +
  // chrome), so it renders in place of the push-modal shell rather than inside
  // the body. Closing it returns to preflight.
  if (phase === 'review-tier-bump' && tierBumpRows) {
    return (
      <TierBumpReviewModal
        rows={tierBumpRows}
        busy={reviewBusy}
        onClose={() => { setTierBumpRows(null); setPhase('preflight'); }}
        onApply={applyTierBumpAndContinue}
      />
    );
  }

  return (
    <div className="modal-backdrop" onClick={backdropClick}>
      <div
        className="modal push-modal"
        role="dialog"
        aria-modal="true"
        aria-label={headTitle}
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 640 }}>
        <div className="modal-head">
          <h3>{headTitle}</h3>
        </div>
        <div className="modal-body">
          {gateWarnings.length > 0 && (
            <div role="alert" style={{ color: 'var(--bad)', fontSize: 13, marginBottom: 10 }}>
              {gateWarnings.map((w, i) => <p key={i} style={{ margin: '2px 0' }}>⚠ {w}</p>)}
            </div>
          )}
          {phase === 'loading' && (
            <div role="status" aria-live="polite" aria-label="Loading" className="skeleton">
              <div className="sk-line w70" />
              <div className="sk-line w40" />
              <div className="sk-line w70" />
            </div>
          )}
          {phase === 'preflight' && renderPreflight()}
          {phase === 'preflight' && renderBatchJobs()}
          {phase === 'review-99487' && renderReview99487()}
          {(phase === 'generating' || phase === 'pushing-signing') && renderRunning()}
          {phase === 'batch-collecting' && (
            <div role="status" aria-live="polite">
              <p>Building batch…</p>
              <div className="skeleton"><div className="sk-line w70" /><div className="sk-line w40" /></div>
            </div>
          )}
          {phase === 'batch-submitted' && (
            <div>
              <p>Batch submitted — <b>{batchCount}</b> record{batchCount === 1 ? '' : 's'} queued as one Bedrock job.</p>
              <p style={{ fontSize: 13, color: 'var(--ink-3)' }}>
                Generation runs asynchronously (typically a few hours). You can close this and
                return later; the job appears under <b>Batch jobs</b> when ready.
              </p>
              {batchError && <p style={{ color: 'var(--bad)', fontSize: 13 }}>{batchError}</p>}
            </div>
          )}
          {phase === 'generated' && renderGenerated()}
          {phase === 'done' && renderDone()}
        </div>
        <div className="modal-foot">
          {phase === 'preflight' && (
            <>
              <button className="btn" onClick={onClose} disabled={reviewBusy}>Cancel</button>
              {mode === 'generate' && (
                <button
                  className="btn primary"
                  onClick={startGenerate}
                  disabled={includedTasks(buckets.toGenerate).length === 0 || reviewBusy}>
                  {reviewBusy ? 'Checking…' : `Generate ${includedTasks(buckets.toGenerate).length}`}
                </button>
              )}
              {BATCH_ENABLED && mode === 'generate' && includedTasks(buckets.toGenerate).length >= BATCH_MIN && (
                <button
                  className="btn"
                  onClick={startBatchGenerate}
                  disabled={reviewBusy}
                  title="Generate this panel as one async Bedrock batch job (~50% cheaper; ready in a few hours)">
                  Batch generate {includedTasks(buckets.toGenerate).length}
                </button>
              )}
              {mode === 'pushsign' && (
                <button
                  className="btn primary"
                  onClick={() => runPushAndSign(includedTasks(buckets.toPushSign))}
                  disabled={includedTasks(buckets.toPushSign).length === 0}>
                  Push &amp; Sign {includedTasks(buckets.toPushSign).length}
                </button>
              )}
            </>
          )}
          {phase === 'review-99487' && (
            <>
              <button className="btn" onClick={onClose} disabled={reviewBusy}>Cancel</button>
              <button
                className="btn primary"
                onClick={applyReviewAndGenerate}
                disabled={reviewBusy}>
                {reviewBusy ? 'Applying…' : `Continue (${
                  includedTasks(buckets.toGenerate).length - Object.values(reviewDecisions).filter((d) => d === 'skip').length
                } tasks)`}
              </button>
            </>
          )}
          {(phase === 'generating' || phase === 'pushing-signing') && (
            <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>Do not close — run in progress.</span>
          )}
          {phase === 'generated' && isBatchSummary && (
            // Batch summary has no per-patient result rows to map back to tasks,
            // so there's no in-place Push & Sign — the user reopens Push to
            // Elation and the now-saved drafts bucket into Push & Sign there.
            <button className="btn primary" onClick={onClose}>Close to Review</button>
          )}
          {phase === 'generated' && !isBatchSummary && (
            <>
              <button className="btn" onClick={onClose}>Close to Review</button>
              <button
                className="btn primary"
                onClick={() => {
                  const okResults = results.filter((r) => r.ok && !r.skipped);
                  const tasksToSign = okResults.map((r) =>
                    (tasks || []).find((t) => t.patientId === r.patientId && t.program === r.program)
                  ).filter(Boolean);
                  runPushAndSign(tasksToSign);
                }}
                disabled={results.filter((r) => r.ok && !r.skipped).length === 0}>
                Push &amp; Sign {results.filter((r) => r.ok && !r.skipped).length}
              </button>
            </>
          )}
          {phase === 'done' && <button className="btn" onClick={onClose}>Close</button>}
        </div>
      </div>
    </div>
  );
}
